import React, { useEffect, useMemo, useState } from "react";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { Autocomplete } from "@material-ui/lab";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { moveScansToDeploymentSale, SaleAction } from "actions";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Button, SecondaryButton } from "components/Form";
import { Label } from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import {
  dateTimeStringToDateTime,
  formatHeaderDateString,
} from "lib/timeFormats";

import { ModalTypes } from "constants/navigation";

import {
  getConsignableSales,
  getConsignableSalesIsFetching,
  getContextByModalType,
  getCurrentSpeciesId,
  getCurrentSaleType,
  getSpecies,
} from "selectors";
import { pluralize } from "lib/pluralize";

interface MoveScansToSaleDialogContext {
  scans?: Scan[];
}

interface MoveScansToSaleDialogProps {
  onClose: () => void;
}

interface SaleOption {
  value: number;
  label: string;
  title: string;
  detail: string;
}

const SaleSelectWrapper = styled.div`
  width: 100%;
`;

const SaleOptionCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray78};
  border-radius: 2px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.titleDark};
  padding: ${({ theme }) => theme.space[2]}px;
  width: 100%;
`;

const SaleOptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const SaleOptionDetail = styled.div`
  color: ${({ theme }) => theme.colors.gray40};
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
`;

function getSaleOptionLabel(
  sale: ConsignableSale,
  deploymentSale: DeploymentSale,
  speciesName: string,
): Pick<SaleOption, "label" | "title" | "detail"> {
  const date = sale.date
    ? formatHeaderDateString(dateTimeStringToDateTime(sale.date))
    : "";
  const saleName = sale.saleTitle || sale.saleType || "Sale";
  const agencyName =
    deploymentSale.livestock_agency_name ||
    deploymentSale.display_name ||
    "Agency";
  const detail = [date, speciesName, sale.saleyardName]
    .filter(Boolean)
    .join(" - ");
  const title = `${saleName} - ${agencyName}`;

  return {
    label: [title, detail].filter(Boolean).join(" - "),
    title,
    detail,
  };
}

function renderSaleOption(saleOption: SaleOption) {
  return (
    <SaleOptionCard>
      <SaleOptionTitle>{saleOption.title}</SaleOptionTitle>
      <SaleOptionDetail>{saleOption.detail}</SaleOptionDetail>
    </SaleOptionCard>
  );
}

export function MoveScansToSaleDialog({ onClose }: MoveScansToSaleDialogProps) {
  const dispatch = useDispatch();
  const context =
    useSelector<RootState, MoveScansToSaleDialogContext | undefined>(
      getContextByModalType(ModalTypes.MoveScansToSale),
    ) || {};
  const { scans = [] } = context;
  const consignableSales = useSelector<
    RootState,
    Record<string | number, ConsignableSale>
  >(getConsignableSales);
  const isFetchingConsignableSales = useSelector<RootState, boolean>(
    getConsignableSalesIsFetching,
  );
  const currentSaleType = useSelector<RootState, string | null>(
    getCurrentSaleType,
  );
  const currentSpeciesId = useSelector<RootState, number | null>(
    getCurrentSpeciesId,
  );
  const speciesById = useSelector<RootState, Record<number, Species>>(
    getSpecies,
  );
  const [selectedDeploymentSaleId, setSelectedDeploymentSaleId] = useState<
    number | null
  >(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    dispatch(SaleAction.fetchConsignableSales({}));
  }, [dispatch]);

  const saleOptions = useMemo<SaleOption[]>(
    () =>
      Object.values(consignableSales)
        .filter(
          sale =>
            sale.saleType === currentSaleType &&
            sale.speciesId === currentSpeciesId,
        )
        .flatMap(sale =>
          sale.deploymentSales.map(deploymentSale => ({
            value: deploymentSale.deployment_sale_id,
            ...getSaleOptionLabel(
              sale,
              deploymentSale,
              speciesById[sale.speciesId]?.name || "",
            ),
          })),
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [consignableSales, currentSaleType, currentSpeciesId, speciesById],
  );

  const selectedSaleOption = saleOptions.find(
    saleOption => saleOption.value === selectedDeploymentSaleId,
  );

  function onSubmit() {
    setIsConfirmOpen(true);
  }

  function onConfirmMoveScans() {
    if (selectedDeploymentSaleId === null || scans.length === 0) {
      return;
    }

    dispatch(moveScansToDeploymentSale(selectedDeploymentSaleId, scans));
    setIsConfirmOpen(false);
    onClose();
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle onClose={onClose}>Move Scans To Sale</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {isFetchingConsignableSales && (
              <Grid item xs={12}>
                Loading sales...
              </Grid>
            )}
            {!isFetchingConsignableSales && saleOptions.length === 0 && (
              <Grid item xs={12}>
                No consignable sales are available.
              </Grid>
            )}
            {!isFetchingConsignableSales && saleOptions.length > 0 && (
              <Grid item xs={12}>
                <SaleSelectWrapper>
                  <Label>Sale</Label>
                  <Autocomplete
                    autoHighlight
                    getOptionLabel={(saleOption: SaleOption) =>
                      saleOption.label
                    }
                    getOptionSelected={(
                      saleOption: SaleOption,
                      value: SaleOption,
                    ) => saleOption.value === value.value}
                    onChange={(ignored, saleOption: SaleOption | null): void =>
                      setSelectedDeploymentSaleId(saleOption?.value || null)
                    }
                    options={saleOptions}
                    renderInput={params => (
                      <TextField {...params} placeholder="Select sale..." />
                    )}
                    renderOption={renderSaleOption}
                    value={selectedSaleOption || null}
                  />
                </SaleSelectWrapper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={onClose} type="button">
            Cancel
          </SecondaryButton>
          <Button
            disabled={!selectedDeploymentSaleId || scans.length === 0}
            onClick={onSubmit}
            type="button"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        title="Confirm Move Scans"
        isOpen={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onDelete={onConfirmMoveScans}
        message={`${scans.length} ${pluralize("scan", scans.length)} will be moved to ${selectedSaleOption?.label}.`}
        buttonMessage="Move Scans"
      />
    </>
  );
}
